/**
 * 演示账号那家酒店的判断口径。
 *
 * 这个名字来自后端的酒店列表，前端只能按名字认人。以前这个判断散在两个文件里各写
 * 一遍字符串（侧边栏的 Quality Standards 菜单、BrandOverview2 的本地 mock 开关），
 * 靠"两处碰巧写得一样"维系 —— 酒店一改名，菜单会直接消失、Portfolio 会转头去打真实
 * 接口，而且两处都不报错，只是东西没了。收到这里，改名只动一行。
 *
 * 用"包含"而不是全等：后端给的名字可能带后缀（分店、城市后缀），全等太脆。
 */
export const DEMO_HOTEL_NAME_MATCH = "Grand Luxury";

/**
 * 当前所选酒店是不是那家演示酒店。
 * hotel 传 store 里的 `hotel/currentHotel`（可能是 undefined，调用方不用先判空）。
 */
export function isDemoHotel(hotel) {
  return String((hotel && hotel.hotelName) || "").includes(DEMO_HOTEL_NAME_MATCH);
}

/**
 * 后端数据里还在用的旧演示品牌名。
 *
 * 前端在 /auth/entities 落库时就把它改写成 DEMO_HOTEL_NAME_MATCH（见 renameDemoBrand），
 * 左上角品牌选择器、登录页品牌列表、客服挂件的实体名因此全都显示新名。这么改不会让
 * 任何请求带着后端不认识的品牌名出去 —— 这个演示账号的组合报表三个接口全部走本地
 * mock，压根不请求（见 BrandOverview2 的 isMockGroupHotel）。
 *
 * 等后端把数据也改过来，这个常量连同下面 isDemoBrandGroup 的旧名兜底一起删掉。
 */
const LEGACY_DEMO_BRAND_NAME = "Hilton";

/**
 * 旧演示品牌名 → 新名；其它名字原样返回。
 * 用全等而不是"包含"：这里改的是要显示出去的完整名字，包含匹配会误伤真实客户。
 */
export function renameDemoBrand(name) {
  return name === LEGACY_DEMO_BRAND_NAME ? DEMO_HOTEL_NAME_MATCH : name;
}

/**
 * 品牌入口下，所选品牌是不是属于那家演示酒店的集团。
 *
 * 纯品牌权限的账号可能根本匹配不到同集团的酒店（`hotel/currentHotel` 为 null，见
 * HotelSelector.vue 里按 hotelGroup 找酒店那段），这时 isDemoHotel 判不出来，只能
 * 靠品牌自己的 hotelGroup 兜住。
 *
 * 复用同一个匹配串：演示账号的酒店名和集团名眼下是同一个词。哪天后端把两者拆成不
 * 一样的名字，这里再单独开一个常量，不要回头在调用方写死字符串 —— 这个文件存在的
 * 理由就是不让这类串散出去。
 *
 * brand 传 `auth.brands` 里的条目（可能是 undefined，调用方不用先判空）。
 */
export function isDemoBrandGroup(brand) {
  // 新旧名都认：hotelGroup 不像 name 那样在前端改写（brandBox.vue 切品牌时要拿它去
  // hotelList 里全等匹配一家酒店，改了就匹配不上），所以后端给的仍可能是旧名。这里
  // 漏判的代价是演示账号转头去打真实接口，宁可两个都认。
  const group = String((brand && brand.hotelGroup) || "");
  return group.includes(DEMO_HOTEL_NAME_MATCH) || group.includes(LEGACY_DEMO_BRAND_NAME);
}
